// ==================== 图片查看器：尺寸 / 缩放 / 平移的纯计算逻辑 ====================
// 与具体 UI 解耦，便于复用（聊天图片、评价图、退款凭证等）与单元测试。

// 数值夹取
export const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// 数值转换：非法值（NaN / null / undefined / ''）回退为默认值（0 是合法值，不会被替换）
const toNumber = (value, fallback) => {
  const num = Number(value)
  return isNaN(num) ? fallback : num
}

// 缩放倍数夹取：非法值按 1 处理，min / max 缺省时按 0.5 ~ 8 处理
export const clampScale = (scale, min, max) => clamp(toNumber(scale, 1), toNumber(min, 0.5), toNumber(max, 8))

/**
 * 计算「适应屏幕 / 适应宽度」后的展示尺寸
 * mode: contain=适应屏幕（完整显示） width=适应宽度（长图放大到与屏幕同宽，纵向可拖动查看）
 * @returns {{width:number, height:number, fitScale:number}} width/height 为展示像素尺寸，
 *          fitScale 为相对原图像素的缩放比（用于「1:1 原始大小」换算）
 */
export const fitSize = ({ naturalWidth, naturalHeight, stageWidth, stageHeight, mode = 'contain' }) => {
  const nw = Number(naturalWidth) || 0
  const nh = Number(naturalHeight) || 0
  const sw = Number(stageWidth) || 0
  const sh = Number(stageHeight) || 0
  if (!nw || !nh || !sw || !sh) return { width: nw, height: nh, fitScale: 1 }

  if (mode === 'width') {
    const width = sw
    const height = sw * nh / nw
    return { width, height, fitScale: width / nw }
  }

  const fitScale = Math.min(sw / nw, sh / nh)
  return { width: nw * fitScale, height: nh * fitScale, fitScale }
}

// 平移边界：当前缩放下图片超出容器的部分（图片不会被拖出屏幕）
export const offsetBounds = ({ baseWidth, baseHeight, scale, stageWidth, stageHeight }) => {
  const w = (Number(baseWidth) || 0) * (Number(scale) || 1)
  const h = (Number(baseHeight) || 0) * (Number(scale) || 1)
  return {
    maxX: Math.max(0, (w - (Number(stageWidth) || 0)) / 2),
    maxY: Math.max(0, (h - (Number(stageHeight) || 0)) / 2)
  }
}

// 把平移量限制在边界内
export const clampOffset = ({ x, y, baseWidth, baseHeight, scale, stageWidth, stageHeight }) => {
  const { maxX, maxY } = offsetBounds({ baseWidth, baseHeight, scale, stageWidth, stageHeight })
  return {
    x: clamp(Number(x) || 0, -maxX, maxX),
    y: clamp(Number(y) || 0, -maxY, maxY)
  }
}

/**
 * 以某锚点缩放：保持锚点在屏幕上的位置不变
 * anchorX / anchorY 为「相对于容器中心」的坐标（鼠标 / 双指中点 - 容器中心）
 */
export const zoomAroundPoint = ({ scale, x, y, targetScale, anchorX, anchorY }) => {
  const from = toNumber(scale, 1)
  const to = toNumber(targetScale, 1)
  const ratio = from ? to / from : 1
  const px = toNumber(x, 0)
  const py = toNumber(y, 0)
  const ax = toNumber(anchorX, 0)
  const ay = toNumber(anchorY, 0)
  return {
    scale: to,
    x: px - (ax - px) * (ratio - 1),
    y: py - (ay - py) * (ratio - 1)
  }
}

// 长图（上下很长，例如超长截图）判断：长宽比超过 ratio 时默认「适应宽度」
export const isLongImage = ({ naturalWidth, naturalHeight, ratio = 2 }) => {
  const nw = Number(naturalWidth) || 0
  const nh = Number(naturalHeight) || 0
  if (!nw || !nh) return false
  return nh / nw > Number(ratio || 2)
}

// 初始纵向偏移：图片比容器高时从顶部开始看（长图默认显示开头内容）
export const topAlignedOffset = ({ baseHeight, scale, stageHeight }) => {
  const h = toNumber(baseHeight, 0) * toNumber(scale, 1)
  const sh = toNumber(stageHeight, 0)
  if (h <= sh) return 0
  return -(h - sh) / 2
}
